import { confirmDialog, guardDialog } from '@adornis/dialog/dialog-functions.js';
import type { SectionRendererSection } from '@adornis/entity-relationship-management/client/details/SectionRenderer.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { AdornisUser } from '@adornis/users/db/a-user.js';
import { html, nothing } from 'lit';
import { getAppAccessStatus, setAppUserArchived, SmartTeamsAppAccessStatus } from '../../api/app-access.js';

/**
 * Read-only diagnostic + archive toggle for a user's SmartTeams app access. Surfaces the
 * answer to "is this person correctly onboarded?": whether they have an app-user record,
 * which userGroup they're in, whether their FE/Customer wiring looks right, and lets the
 * admin archive them so the FE app's login hook starts rejecting them.
 *
 * Pure query/UI section — no scope or role check, just read & flip the archived flag.
 */
const AppAccessEditor = stateful(({ user }: { user: AdornisUser<any> }) => {
  const [refreshKey, setRefreshKey] = useState(0);
  const [busy, setBusy] = useState(false);

  const statusQuery = useQuery<SmartTeamsAppAccessStatus | null>(
    () =>
      getAppAccessStatus(user._id)({
        hasAppAccess: 1,
        userGroup: 1,
        archived: 1,
        mustChangePassword: 1,
        serviceNowFEId: 1,
        hasFieldEngineerRecord: 1,
        fieldEngineerName: 1,
        linkedCustomerCount: 1,
      }),
    [user._id, refreshKey],
  );

  if (statusQuery.loading) {
    return html`<span class="loading loading-spinner loading-sm text-primary"></span>`;
  }

  const status = statusQuery.value;

  if (!status?.hasAppAccess) {
    return html`
      <div class="flex flex-col gap-2">
        <div class="flex items-center gap-2 text-sm text-base-content/70">
          <span class="icon-[mdi--account-off-outline] text-lg"></span>
          <span>No SmartTeams app access.</span>
        </div>
        <p class="text-xs text-base-content/60">
          This user has an account but cannot log into the SmartTeams app. Add them as a
          Field Engineer (in the Field Engineers section) or as a Customer (assign them
          the Customer role and link customers below).
        </p>
      </div>
    `;
  }

  const groupBadge = (() => {
    const g = status.userGroup;
    if (!g) return html`<span class="badge badge-ghost">Unknown</span>`;
    const colorMap: Record<string, string> = {
      Admin: 'badge-primary',
      FieldEngineer: 'badge-info',
      Customer: 'badge-success',
      PartnerManager: 'badge-warning',
    };
    return html`<span class="badge ${colorMap[g] ?? 'badge-neutral'}">${g}</span>`;
  })();

  const handleToggleArchive = async () => {
    const next = !status.archived;
    if (next) {
      const ok = await confirmDialog(
        html`<p>Archive this user's app access? They will no longer be able to log into the SmartTeams app.</p>`,
        { acceptText: 'Archive', highlightDecline: true },
      ).catch(() => false);
      if (!ok) return;
    }
    setBusy(true);
    await guardDialog(() => setAppUserArchived(user._id, next)(), {
      loadingText: next ? 'Archiving…' : 'Reactivating…',
      successText: next ? 'Archived. Login will be blocked.' : 'Reactivated.',
      errorMessagePrefix: next ? 'Could not archive' : 'Could not reactivate',
    });
    setBusy(false);
    setRefreshKey(refreshKey + 1);
  };

  // Diagnostic warning lines for misconfigurations.
  const warnings: string[] = [];
  if (status.userGroup === 'FieldEngineer' && !status.hasFieldEngineerRecord) {
    warnings.push('Field Engineer login without a SmartTeamsFieldEngineer record — they will see an empty ticket list.');
  }
  if (status.userGroup === 'FieldEngineer' && !status.serviceNowFEId) {
    warnings.push('No ServiceNow Field Engineer linked — assigned tickets will not show up.');
  }
  if (status.userGroup === 'Customer' && status.linkedCustomerCount === 0) {
    warnings.push('Customer login with zero linked customers — they will see an empty ticket list.');
  }

  return html`
    <div class="flex flex-col gap-3">
      <div class="flex items-center gap-3 flex-wrap">
        <div class="flex items-center gap-2">
          <span class="icon-[mdi--account-check-outline] text-lg text-success"></span>
          <span class="text-sm font-medium">Has app access</span>
        </div>
        <span>·</span>
        ${groupBadge}
        ${status.archived
          ? html`<span class="badge badge-error">Archived</span>`
          : html`<span class="badge badge-ghost">Active</span>`}
        ${status.mustChangePassword
          ? html`<span class="badge badge-warning" title="User must change password at first login"
              >Password change required</span
            >`
          : nothing}
      </div>

      ${status.userGroup === 'FieldEngineer'
        ? html`
            <div class="text-xs text-base-content/70 flex flex-col gap-1">
              ${status.hasFieldEngineerRecord
                ? html`<div>FE record: <span class="font-medium">${status.fieldEngineerName ?? '(unnamed)'}</span></div>`
                : html`<div class="text-warning">No SmartTeamsFieldEngineer record exists for this user.</div>`}
              ${status.serviceNowFEId
                ? html`<div>
                    SN FE sys_id: <span class="font-mono">${status.serviceNowFEId.slice(0, 8)}…</span>
                  </div>`
                : nothing}
            </div>
          `
        : nothing}
      ${status.userGroup === 'Customer'
        ? html`
            <div class="text-xs text-base-content/70">
              Linked customer organisations:
              <span class="font-medium">${status.linkedCustomerCount ?? 0}</span>
            </div>
          `
        : nothing}
      ${warnings.length > 0
        ? html`
            <div class="alert alert-warning text-sm">
              <span class="icon-[mdi--alert-outline] text-lg"></span>
              <ul class="flex flex-col gap-1">
                ${warnings.map(w => html`<li>${w}</li>`)}
              </ul>
            </div>
          `
        : nothing}

      <div>
        <button
          class="btn btn-sm ${status.archived ? 'btn-success' : 'btn-error btn-outline'}"
          ?disabled=${busy}
          @click=${handleToggleArchive}
        >
          ${busy
            ? html`<span class="loading loading-spinner loading-xs"></span>`
            : status.archived
              ? html`<span class="icon-[mdi--account-reactivate-outline] text-base"></span>`
              : html`<span class="icon-[mdi--account-cancel-outline] text-base"></span>`}
          ${status.archived ? 'Reactivate app access' : 'Archive app access'}
        </button>
      </div>
    </div>
  `;
});

export const appAccessSection: SectionRendererSection<AdornisUser> = {
  title: 'SmartTeams App Access',
  render: ({ entity: user }) => {
    if (!user) return nothing;
    return AppAccessEditor({ user });
  },
};
